import { useState } from "react";
import { Navigate } from "react-router-dom";
import { LayoutDashboard, Map, Globe, Inbox, FileText, Images, Palette, Tag, Search, Users, BookOpen, BusFront, BarChart3, Sparkles, Wand2, LayoutTemplate, Clapperboard, ClipboardList, BadgePercent } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { PageShell } from "../components/landing/PageShell";
import { DashboardTab } from "../components/admin/DashboardTab";
import { ToursTab } from "../components/admin/ToursTab";
import { DestinationsTab } from "../components/admin/DestinationsTab";
import { BookingsTab } from "../components/admin/BookingsTab";
import { WebsiteTab } from "../components/admin/WebsiteTab";
import { MediaTab } from "../components/admin/MediaTab";
import { DesignTab } from "../components/admin/DesignTab";
import { PricingTab } from "../components/admin/PricingTab";
import { SeoTab } from "../components/admin/SeoTab";
import { GscTab } from "../components/admin/GscTab";
import { ThemesTab } from "../components/admin/ThemesTab";
import { ThemeStudioTab } from "../components/admin/ThemeStudioTab";
import { WebsiteStudioTab } from "../components/admin/WebsiteStudioTab";
import { SeasonalAnimationTab } from "../components/admin/SeasonalAnimationTab";
import { BookTourManagerTab } from "../components/admin/BookTourManagerTab";
import { SpecialOffersTab } from "../components/admin/SpecialOffersTab";
import { BlogTab } from "../components/admin/BlogTab";
import { FleetTab } from "../components/admin/FleetTab";
import { UsersTab } from "../components/admin/UsersTab";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "tours", label: "Tours", icon: Map },
  { id: "destinations", label: "Destinations", icon: Globe },
  { id: "bookings", label: "Bookings", icon: Inbox },
  { id: "blog", label: "Blog", icon: BookOpen },
  { id: "fleet", label: "Fleet", icon: BusFront },
  { id: "website", label: "Website Editor", icon: FileText },
  { id: "media", label: "Media", icon: Images },
  { id: "design", label: "Design", icon: Palette },
  { id: "pricing", label: "Pricing", icon: Tag },
  { id: "seo", label: "SEO", icon: Search },
  { id: "search", label: "Search", icon: BarChart3 },
  { id: "themes", label: "Themes", icon: Sparkles },
  { id: "theme-studio", label: "Design Themes", icon: Wand2 },
  { id: "animation-studio", label: "Animation Studio", icon: Clapperboard },
  { id: "website-studio", label: "Website Studio", icon: LayoutTemplate },
  { id: "booking-manager", label: "Book a Tour", icon: ClipboardList },
  { id: "special-offers", label: "Special Offers", icon: BadgePercent },
  { id: "users", label: "Users", icon: Users },
];

export const AdminPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="admin-loading-spinner" />
      </div>
    );
  }
  if (user === false || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <PageShell title="Admin Dashboard" subtitle="Full control of your website — no code needed.">
      <div className="flex flex-col lg:flex-row gap-8" data-testid="admin-dashboard-layout">
        <nav className="lg:w-56 shrink-0 flex lg:flex-col gap-1.5 overflow-x-auto pb-2 lg:pb-0" data-testid="admin-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-tab-${s.id}`}
              onClick={() => setTab(s.id)}
              className={`inline-flex items-center gap-2.5 rounded-2xl px-4 py-3 text-sm font-bold whitespace-nowrap transition-all duration-300 text-left ${
                tab === s.id
                  ? "bg-[var(--brand)] text-white shadow-lg shadow-[var(--brand)]/30"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-[var(--brand)] hover:text-[var(--brand)]"
              }`}
            >
              <s.icon className="w-4 h-4 shrink-0" />
              {s.label}
            </button>
          ))}
        </nav>
        <div className="flex-1 min-w-0" data-testid="admin-panel-content">
          {tab === "dashboard" && <DashboardTab />}
          {tab === "tours" && <ToursTab />}
          {tab === "destinations" && <DestinationsTab />}
          {tab === "bookings" && <BookingsTab />}
          {tab === "blog" && <BlogTab />}
          {tab === "fleet" && <FleetTab />}
          {tab === "website" && <WebsiteTab />}
          {tab === "media" && <MediaTab />}
          {tab === "design" && <DesignTab />}
          {tab === "pricing" && <PricingTab />}
          {tab === "seo" && <SeoTab />}
          {tab === "search" && <GscTab />}
          {tab === "themes" && <ThemesTab />}
          {tab === "theme-studio" && <ThemeStudioTab />}
          {tab === "animation-studio" && <SeasonalAnimationTab />}
          {tab === "booking-manager" && <BookTourManagerTab />}
          {tab === "special-offers" && <SpecialOffersTab />}
          {tab === "website-studio" && <WebsiteStudioTab />}
          {tab === "users" && <UsersTab />}
        </div>
      </div>
    </PageShell>
  );
};
